import React, { useState } from 'react';
import { Card, SubjectType } from '../../types/Card';
import { ExportOptions } from '../../types/ApiResponses';
import { apiClient } from '../../services/apiClient';

interface PDFExporterProps {
  cards: Card[];
  onClose: () => void;
}

export const PDFExporter: React.FC<PDFExporterProps> = ({ cards, onClose }) => {
  const [pinnedOnly, setPinnedOnly] = useState(false);
  const [selectedSubjects, setSelectedSubjects] = useState<SubjectType[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string>('');

  // Get unique subjects from cards
  const availableSubjects = Array.from(new Set(cards.map(card => card.subject)));

  // Filter cards based on current selections
  const getFilteredCards = (): Card[] => {
    let filtered = cards;

    if (pinnedOnly) {
      filtered = filtered.filter(card => card.isPinned);
    }

    if (selectedSubjects.length > 0) {
      filtered = filtered.filter(card => selectedSubjects.includes(card.subject));
    }

    return filtered;
  };

  const filteredCards = getFilteredCards();

  const handleSubjectToggle = (subject: SubjectType) => {
    setSelectedSubjects(prev => 
      prev.includes(subject)
        ? prev.filter(s => s !== subject)
        : [...prev, subject]
    );
  };

  const handleExport = async () => {
    if (filteredCards.length === 0) {
      setError('No cards to export with current filters');
      return;
    }

    setIsExporting(true);
    setError('');

    try {
      const options: ExportOptions = {
        pinnedOnly,
        subjects: selectedSubjects.length > 0 ? selectedSubjects : undefined,
        includeInstructions: true,
      };

      const response = await apiClient.exportToPDF(filteredCards, options);
      
      // Trigger download
      window.open(response.downloadUrl, '_blank');
      
      // Close modal after successful export
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to export PDF';
      setError(errorMessage);
    } finally {
      setIsExporting(false);
    }
  };

  const subjectLabels: Record<SubjectType, { label: string; emoji: string }> = {
    english:   { label: 'English',             emoji: '📚' },
    maths:     { label: 'Mathematics',          emoji: '🔢' },
    science:   { label: 'Science',              emoji: '🔬' },
    history:   { label: 'History',              emoji: '🏛️' },
    geography: { label: 'Geography',            emoji: '🌍' },
    computing: { label: 'Computing',            emoji: '💻' },
    art:       { label: 'Art & Design',         emoji: '🎨' },
    music:     { label: 'Music',                emoji: '🎵' },
    pe:        { label: 'P.E.',                 emoji: '⚽' },
    dt:        { label: 'Design & Technology',  emoji: '🔧' },
    re:        { label: 'R.E.',                 emoji: '🕊️' },
    pshe:      { label: 'PSHE',                 emoji: '💬' },
    mfl:       { label: 'Modern Languages',     emoji: '🌐' },
    space:     { label: 'Space',                emoji: '🚀' },
  };

  return (
    <div className="pdf-exporter-overlay" onClick={onClose}>
      <div className="pdf-exporter-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pdf-exporter-header">
          <h2>Export to PDF</h2>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        <div className="pdf-exporter-content">
          {error && (
            <div className="export-error">
              <span>{error}</span>
            </div>
          )}

          <div className="export-filters">
            <h3>Filter Options</h3>

            <div className="filter-option">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={pinnedOnly}
                  onChange={(e) => setPinnedOnly(e.target.checked)}
                />
                <span>Pinned cards only</span>
              </label>
              <p className="filter-description">
                Export only cards you've pinned for quick access
              </p>
            </div>

            <div className="filter-option">
              <h4>Filter by Subject</h4>
              <div className="subject-filters">
                {availableSubjects.map(subject => (
                  <label key={subject} className="subject-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedSubjects.includes(subject)}
                      onChange={() => handleSubjectToggle(subject)}
                    />
                    <span>
                      {subjectLabels[subject].emoji} {subjectLabels[subject].label}
                    </span>
                  </label>
                ))}
              </div>
              <p className="filter-description">
                Select specific subjects to include in the export
              </p>
            </div>
          </div>

          <div className="export-preview">
            <h3>Preview</h3>
            <div className="preview-summary">
              <div className="preview-stat">
                <span className="stat-value">{filteredCards.length}</span>
                <span className="stat-label">cards will be exported</span>
              </div>
              <div className="preview-stat">
                <span className="stat-value">{Math.ceil(filteredCards.length / 2)}</span>
                <span className="stat-label">pages (2 cards per page)</span>
              </div>
            </div>

            {filteredCards.length > 0 ? (
              <div className="preview-cards">
                <h4>Cards to Export:</h4>
                <ul className="preview-card-list">
                  {filteredCards.slice(0, 10).map(card => (
                    <li key={card.id} className="preview-card-item">
                      <span className="preview-emoji">{card.emoji}</span>
                      <span className="preview-topic">{card.topic}</span>
                      <span className="preview-subject">({subjectLabels[card.subject].label})</span>
                      {card.isPinned && <span className="preview-pin">📌</span>}
                    </li>
                  ))}
                  {filteredCards.length > 10 && (
                    <li className="preview-more">
                      + {filteredCards.length - 10} more cards
                    </li>
                  )}
                </ul>
              </div>
            ) : (
              <div className="preview-empty">
                <p>No cards match your current filters.</p>
                <p>Adjust your filters to include cards in the export.</p>
              </div>
            )}
          </div>
        </div>

        <div className="pdf-exporter-footer">
          <button 
            className="cancel-button" 
            onClick={onClose}
            disabled={isExporting}
          >
            Cancel
          </button>
          <button 
            className="export-button" 
            onClick={handleExport}
            disabled={isExporting || filteredCards.length === 0}
          >
            {isExporting ? 'Exporting...' : `Export ${filteredCards.length} Card${filteredCards.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};
